import Button from '$components/Buttons/Button';
import { useSaveAdditionalDocumentMutation } from '$store/modules/additional-documents';
import {
  GetCertificationsOutput,
  useAddCertificationMutation,
  useUpdateCertificationMutation,
} from '$store/modules/certifications';
import { errorHandler, successHandler } from '$store/utils';
import { AdditionalDocument } from '$types/candidates';
import { Calendar } from 'primereact/calendar';
import { FloatLabel } from 'primereact/floatlabel';
import { InputText } from 'primereact/inputtext';
import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type CandidateCertificationFormType = {
  fileMethod: 'newUpload' | 'selectExistingFile';
  file: FileList | null;
  candidateFile: number | null;
  CertificationName: string;
  IssuingOrganization: string;
  IssueDate: Date | null;
  ExpiryDate: Date | null;
  CredentialId: string;
  CredentialUrl: string;
  Description: string;
};

const CandidateUpsertCertification = ({
  certificationFiles,
  onComplete,
  candidateId,
  certificationFileCategoryId,
  selectedCertification,
}: {
  certificationFiles: AdditionalDocument[];
  onComplete: () => void;
  candidateId: number;
  certificationFileCategoryId: number;
  selectedCertification: undefined | GetCertificationsOutput[number];
}) => {
  const [t] = useTranslation('global');
  const [addCandidateCertification, { isLoading: isLoadingAddCertification }] =
    useAddCertificationMutation();
  const [updateCertification, { isLoading: isUpdatingCertification }] =
    useUpdateCertificationMutation();

  const [
    saveAdditionalDocument,
    { isLoading: isLoadingSaveAdditionalDocument },
  ] = useSaveAdditionalDocumentMutation();

  const hasCertifications = useMemo(() => {
    return certificationFiles.length !== 0 || selectedCertification;
  }, [certificationFiles.length, selectedCertification]);

  const {
    register,
    handleSubmit,
    setValue,
    control,
    formState: { errors },
    setError,
  } = useForm<CandidateCertificationFormType>({
    defaultValues: {
      fileMethod:
        certificationFiles.length !== 0 || selectedCertification
          ? 'selectExistingFile'
          : 'newUpload',
      file: null,
      candidateFile: selectedCertification ? selectedCertification.FileId : -1,
      CertificationName: selectedCertification?.CertificationName || '',
      IssuingOrganization: selectedCertification?.IssuingOrganization || '',
      IssueDate: selectedCertification?.IssueDate
        ? new Date(selectedCertification.IssueDate)
        : null,
      ExpiryDate: selectedCertification?.ExpiryDate
        ? new Date(selectedCertification.ExpiryDate)
        : null,
      CredentialId: selectedCertification?.CredentialId || '',
      CredentialUrl: selectedCertification?.CredentialUrl || '',
      Description: selectedCertification?.Description || '',
    },
  });

  const onSubmitCertificate = async (data: CandidateCertificationFormType) => {
    if (
      data.fileMethod === 'newUpload' &&
      (!data.file || data.file.length === 0)
    ) {
      setError('file', {
        type: 'missingFile',
        message: 'You must select a file',
      });
      return;
    }

    if (data.fileMethod === 'selectExistingFile' && data.candidateFile === -1) {
      setError('candidateFile', {
        type: 'fileNotSelected',
        message: 'You must select a file',
      });
      return;
    }

    let fileId = data.candidateFile;

    if (data.fileMethod === 'newUpload') {
      const formData = new FormData();
      formData.append('CandidateId', `${candidateId}`);
      formData.append('Description', data.CertificationName);
      formData.append('FileCategory', `${certificationFileCategoryId}`);
      formData.append('IsCandidatesVisible', '1');
      formData.append('IsEmployersVisible', '1');

      formData.append('file', data.file![0]);

      await saveAdditionalDocument({
        data: formData,
      }).then((resp) => {
        if ('error' in resp) {
          errorHandler(
            '[CandidateAddNewCertification Certification Upload]',
            'Something went wrong. Try again later',
            true,
            true
          );
          return;
        }

        fileId = resp.data.Id;
      });
    }

    /** Edit */
    if (selectedCertification) {
      await updateCertification({
        CandidateCertificationId: selectedCertification.Id,
        CertificationName: data.CertificationName,
        CredentialId: data.CredentialId,
        CredentialUrl: data.CredentialUrl || undefined,
        ExpiryDate: data.ExpiryDate?.toISOString() || undefined,
        FileId: fileId as number,
        Description: data.Description,
        IssuingOrganization: data.IssuingOrganization,
        IssueDate: data.IssueDate?.toISOString() || undefined,
      })
        .then((resp) => {
          if ('error' in resp) throw new Error('Something went wrong');
          successHandler('Certification updated successfully', true, true);
          onComplete();
        })
        .catch(() => {
          errorHandler(
            '[CandidateAddNewCertification Certification Upload]',
            'Something went wrong. Try again later',
            true,
            true
          );
        });
    } else {
      /** Add new certification */
      await addCandidateCertification({
        CandidateId: candidateId,
        CertificationName: data.CertificationName,
        CredentialId: data.CredentialId,
        CredentialUrl: data.CredentialUrl || undefined,
        ExpiryDate: data.ExpiryDate?.toISOString() || undefined,
        FileId: fileId as number,
        Description: data.Description,
        IssuingOrganization: data.IssuingOrganization,
        IssueDate: data.IssueDate?.toISOString() || undefined,
      })
        .then((resp) => {
          if ('error' in resp) throw new Error('Something went wrong');
          successHandler('Certification added successfully', true, true);
          onComplete();
        })
        .catch(() => {
          errorHandler(
            '[CandidateAddNewCertification Certification Upload]',
            'Something went wrong. Try again later',
            true,
            true
          );
        });
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmitCertificate)}
      className="flex flex-col gap-4 text-sm"
    >
      <fieldset
        disabled={
          isLoadingAddCertification ||
          isLoadingSaveAdditionalDocument ||
          isUpdatingCertification
        }
        className="flex flex-col gap-4"
      >
        <Controller
          name="fileMethod"
          control={control}
          shouldUnregister
          render={({ field }) => {
            const options = certificationFiles?.map((singleCertification) => (
              <option
                value={singleCertification.Id}
                key={singleCertification.Id}
                className="bg-gray-200"
              >
                {singleCertification.Description}
              </option>
            ));
            return (
              <div className="flex flex-row gap-4 items-start">
                <label
                  className={`gap-2 flex ${
                    !hasCertifications &&
                    'text-gray-400 hover:cursor-not-allowed'
                  } items-center`}
                >
                  <input
                    type="radio"
                    {...field}
                    disabled={!hasCertifications}
                    checked={field.value === 'selectExistingFile'}
                    value="selectExistingFile"
                  />
                  {t('CandidateCertifications.selectExitingFile')}
                </label>
                <div className="flex flex-col items-start">
                  <select
                    {...register('candidateFile', {
                      required:
                        field.value === 'selectExistingFile'
                          ? t('CandidateCertifications.Error.emptyFile')
                          : false,
                    })}
                    className={`py-1 px-2 text-sm rounded bg-gray-200 ${
                      !hasCertifications &&
                      'opacity-50 hover:cursor-not-allowed'
                    }`}
                    {...(!hasCertifications && {
                      disabled: true,
                    })}
                  >
                    {!hasCertifications && (
                      <option
                        hidden
                        value={-1}
                        className="bg-gray-200 hover:bg-gray-200"
                      >
                        {t(
                          'CandidateCertifications.noUnregisteredCertifications'
                        )}
                      </option>
                    )}
                    {hasCertifications && (
                      <option
                        hidden
                        value={-1}
                        className="bg-gray-200 hover:bg-gray-200"
                      >
                        {t('CandidateCertifications.selectFile')}
                      </option>
                    )}
                    {options}
                  </select>
                  {errors.candidateFile?.message && (
                    <span className="text-red-400 text-xs">
                      *{errors.candidateFile?.message}
                    </span>
                  )}
                </div>
              </div>
            );
          }}
        />

        <Controller
          name="fileMethod"
          control={control}
          render={({ field }) => (
            <div className="flex flex-row gap-4 items-start">
              <label className="flex gap-2">
                <input
                  {...field}
                  type="radio"
                  value="newUpload"
                  checked={field.value === 'newUpload'}
                />
                {t('CandidateCertifications.uploadNewFile')}
              </label>
              <div className="flex flex-col items-start">
                <input
                  type="file"
                  {...register('file', {
                    required:
                      field.value === 'newUpload'
                        ? t('CandidateCertifications.Error.emptyFile')
                        : false,
                  })}
                  className="text-xs"
                  accept="application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={() => setValue('fileMethod', 'newUpload')}
                />
                {errors.file?.message && (
                  <span className="text-red-400 text-xs">
                    *{errors.file?.message}
                  </span>
                )}
              </div>
            </div>
          )}
        />

        <div className="mt-4 flex flex-row gap-4 justify-between">
          <FloatLabel>
            <label htmlFor="CertificationName">
              {t('CandidateCertifications.certificationName')}
            </label>
            <InputText
              {...register('CertificationName', {
                required: t('CandidateCertifications.required'),
              })}
              invalid={!!errors.CertificationName?.message}
            />
          </FloatLabel>
          <FloatLabel className="w-full">
            <InputText {...register('Description')} className="w-full" />
            <label htmlFor="Description">
              {t('CandidateCertifications.description')}
            </label>
          </FloatLabel>
        </div>
        <div className="mt-4 flex flex-row gap-4 justify-between">
          <FloatLabel className="w-1/3">
            <label htmlFor="IssuingOrganization">
              {t('CandidateCertifications.issuingOrganization')}
            </label>
            <InputText
              {...register('IssuingOrganization')}
              className="w-full"
            />
          </FloatLabel>
          <FloatLabel>
            <InputText {...register('CredentialId')} />
            <label htmlFor="CredentialId">
              {t('CandidateCertifications.credentialID')}
            </label>
          </FloatLabel>

          <FloatLabel className="w-1/4">
            <InputText {...register('CredentialUrl')} className="w-full" />
            <label htmlFor="CredentialUrl">
              {t('CandidateCertifications.credentialURL')}
            </label>
          </FloatLabel>
          <Controller
            name="IssueDate"
            control={control}
            render={({ field }) => (
              <FloatLabel>
                <Calendar
                  {...field}
                  id="IssueDate"
                  onChange={(e) => field.onChange(e.value)}
                  maxDate={new Date()}
                />
                <label htmlFor="IssueDate">
                  {t('CandidateCertifications.issueDate')}
                </label>
              </FloatLabel>
            )}
          />

          <Controller
            name="ExpiryDate"
            control={control}
            render={({ field }) => (
              <FloatLabel>
                <Calendar
                  {...field}
                  id="ExpiryDate"
                  onChange={(e) => field.onChange(e.value)}
                />
                <label htmlFor="ExpiryDate">
                  {t('CandidateCertifications.expiryDate')}
                </label>
              </FloatLabel>
            )}
          />
        </div>
        <div className="flex flex-row gap-4 w-1/4 ml-auto">
          <Button
            bgColor="#afafaf"
            showText
            text={t('common.cancel')}
            invertTextContent
            styling="px-4 whitespace-nowrap"
            onClick={onComplete}
          />
          <Button
            bgColor="#079e88"
            showText
            text={t('common.save')}
            styling="px-4 whitespace-nowrap"
            submit
          />
        </div>
      </fieldset>
    </form>
  );
};

export default CandidateUpsertCertification;
